import { useMemo, useState } from "react";
import { FaRegEdit } from "react-icons/fa";
import { HiOutlineBuildingStorefront, HiOutlineMagnifyingGlass } from "react-icons/hi2";
import { IClinicOption } from "@/api_framework/api_modals/ClinicTagConfig";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import { SearchInput } from "../HomeTileConfig/controls";
import { matchesSearch, secondaryButtonClassName, toSearchKey } from "../HomeTileConfig/interface";

export const ClinicList = ({
  clinics,
  isLoading,
  onEdit,
}: {
  clinics: IClinicOption[];
  isLoading: boolean;
  onEdit: (clinicId: string) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");

  const visibleClinics = useMemo(
    () =>
      clinics.filter(
        (clinic) =>
          matchesSearch(searchTerm, [clinic.clinic_name]) || toSearchKey(clinic.clinic_id) === toSearchKey(searchTerm),
      ),
    [clinics, searchTerm],
  );

  const columns = useMemo<TableColumn<IClinicOption>[]>(
    () => [
      {
        id: "clinic_name",
        header: "Clinic",
        minWidth: 260,
        cell: ({ row }) => <span className="text-xs font-semibold text-slate-800">{row.clinic_name || "—"}</span>,
        headerClassName: "text-slate-500",
      },
      {
        id: "action",
        header: "Action",
        align: "center",
        width: 130,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onEdit(row.clinic_id)}
            className="rounded-lg border border-transparent p-2 text-[#193A6A] transition-colors hover:border-slate-200 hover:bg-slate-50"
            aria-label={`Edit tag configuration for ${row.clinic_name}`}
            title="Edit tag configuration"
          >
            <FaRegEdit className="h-4 w-4" />
          </button>
        ),
        headerClassName: "text-slate-500 text-center",
      },
    ],
    [onEdit],
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-slate-100 px-5 py-4">
        <div>
          <div className="text-sm font-semibold text-gray-900">Clinics</div>
          <p className="mt-0.5 text-[11px] text-gray-500">Pick a clinic to configure the tags shown on it.</p>
        </div>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search clinic name or ID"
          ariaLabel="Search clinics"
          clearLabel="Clear clinic search"
        />
      </div>

      {isLoading ? (
        <div className="px-5 py-4">
          <StructuredTableSkeleton columns={columns.length} />
        </div>
      ) : visibleClinics.length ? (
        <TableComponent<IClinicOption>
          columns={columns}
          data={visibleClinics}
          getRowId={(row) => row.clinic_id}
          containerClassName="!mr-0 !rounded-none !border-0 bg-white"
          scrollContainerClassName="overflow-x-auto max-h-[calc(100vh-280px)]"
          minTableWidthClassName="min-w-full"
          tableClassName="min-w-full divide-y divide-slate-100"
          stickyHeader
          wrapCells
        />
      ) : (
        <div className="flex flex-col items-center px-5 py-14 text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF5FF] text-[#193A6A]">
            {searchTerm.trim() ? (
              <HiOutlineMagnifyingGlass className="h-6 w-6" />
            ) : (
              <HiOutlineBuildingStorefront className="h-6 w-6" />
            )}
          </span>
          <h3 className="mt-3 text-sm font-semibold text-gray-900">
            {searchTerm.trim() ? "No clinic found" : "No clinics available"}
          </h3>
          {searchTerm && (
            <button type="button" onClick={() => setSearchTerm("")} className={`mt-4 ${secondaryButtonClassName}`}>
              Clear search
            </button>
          )}
        </div>
      )}
    </>
  );
};
